/**
 * Consult room lifecycle — the ONE resolution of "where in its life is this room?".
 *
 * Source of truth: `docs/booking-module/telehealth-room-lifecycle.md` §5 (the wire shape)
 * and §11 (this module). `GET /consults/{id}/status` publishes three instants — an
 * `endingSoonAt`, a `scheduledEndAt` and a `closesAt` (the HARD CLOSE, `scheduled_end +
 * hardCloseGraceMinutes`) — plus a `presence` sub-document written by the `room_watch`
 * job. Every surface that renders a sentence about the end of a consultation resolves it
 * here, so the patient's frame and the practitioner's pills cannot disagree.
 *
 * PURE, AND I/O-FREE. `nowMs` is passed in: the caller owns the clock (and the server
 * offset), which is what lets a test drive every boundary without faking timers, and
 * what keeps two elements painted in one render from being classified against two
 * different instants.
 *
 * PHASE AND SOLO ARE ORTHOGONAL AXES, and this is the reason they are two fields rather
 * than one enum. A lone participant can be alone while the call is `live`, while it is
 * `endingSoon` *or* while it is in `overrun`; folding "solo" into the phase enum forces a
 * precedence choice, and whichever way that choice falls it silently deletes one of two
 * sentences that are both true.
 */
import { isFixedBlock } from "./contract";

/** Ordered most-open to most-closed. `unknown` is "no status has landed yet". */
export const ROOM_PHASES = ["unknown", "waiting", "live", "endingSoon", "overrun", "closed"];

// The two session states in which THE CALL IS STILL A CALL — stated positively, as the
// LIVE set rather than the over set, so a state the backend adds later reads as "over"
// and takes the room down instead of leaving a dead one mounted. The safe default is the
// one that shuts a door.
//
// It is NOT `SESSION_MONEY_FINAL_STATES` (contract.js), and reusing that set here would
// be wrong on the meaning: those three are the states in which the MONEY will not change
// again, and `computing`/`computed` are deliberately absent from them — a bill still
// settling. But a consultation whose bill is being computed is a consultation that is
// OVER, and nobody must still be sitting in its room while it settles.
const SESSION_LIVE_STATES = ["scheduled", "in_progress"];

/**
 * IS THIS CONSULTATION OVER? The server's own two facts, one per pricing model, and never
 * a local clock:
 *   * a fixed block has no session, so its state is its appointment's — `completed` is
 *     stamped when the door shuts;
 *   * a metered consult is over the moment its session leaves the live set.
 *
 * Lifted out of `public/ConsultRoomFrame.jsx` (BUG-055) so the frame, this module's phase
 * resolution and the practitioner's page share ONE definition rather than three.
 *
 * @param {?object} status a `GET /consults/{id}/status` payload
 */
export const isConsultOver = (status) => status != null && (
  isFixedBlock(status)
    ? status.bookingStatus === "completed"
    : (status.sessionStatus != null && !SESSION_LIVE_STATES.includes(status.sessionStatus)));

/**
 * An ISO instant as epoch milliseconds, or null.
 *
 * NULLS REMOVE A BOUNDARY AND NEVER SYNTHESISE ONE. An absent `closesAt` means "this
 * client cannot say when the room shuts", and the answer to that is to stop asking the
 * question — never to compute `scheduledEndAt + 10 minutes` here, because the grace is
 * `hardCloseGraceMinutes`, an operator-editable setting the browser does not hold. An
 * unparseable string is the same absence: a boundary we cannot read is a boundary we do
 * not have.
 */
const toMs = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Whole seconds from `nowMs` to `targetMs`, rounded UP.
 *
 * `ceil` and not `round` or `floor`: a countdown must never read 0 while the event is
 * still in the future — with 400 ms left, "0 s" states an instant that has not happened.
 * Clamped at 0 because a negative countdown is not a sentence anyone should render; the
 * phase, not the sign of this number, is what says the instant has passed.
 */
const secondsUntil = (targetMs, nowMs) =>
  (targetMs == null ? null : Math.max(0, Math.ceil((targetMs - nowMs) / 1000)));

const EMPTY_SOLO = {
  active: false, disconnectAt: null, secondsLeft: null, presentRoles: [], snapshotAt: null,
};
const EMPTY_EJECTED = { active: false, at: null };

/**
 * Resolve one `/status` payload plus one instant into everything a room surface renders.
 *
 * @param {?object} status a `GET /consults/{id}/status` payload; null before the first poll
 * @param {number} nowMs the caller's clock, server-corrected — `Date.now() + serverOffset`
 * @returns {{
 *   phase: string, endsAt: ?string, closesAt: ?string,
 *   secondsToEnd: ?number, secondsToClose: ?number,
 *   solo: {active: boolean, disconnectAt: ?string, secondsLeft: ?number,
 *          presentRoles: string[], snapshotAt: ?string},
 *   ejected: {active: boolean, at: ?string},
 *   countdownActive: boolean,
 * }}
 */
export function roomPhase(status, nowMs) {
  if (status == null) {
    return {
      phase: "unknown", endsAt: null, closesAt: null,
      secondsToEnd: null, secondsToClose: null,
      solo: EMPTY_SOLO, ejected: EMPTY_EJECTED, countdownActive: false,
    };
  }

  const endsAt = status.scheduledEndAt || null;
  const closesAt = status.closesAt || null;
  const endingSoonAt = status.endingSoonAt || null;
  const endsMs = toMs(endsAt);
  const closesMs = toMs(closesAt);
  const endingSoonMs = toMs(endingSoonAt);

  // RESOLUTION, MOST-CLOSED FIRST. Read top to bottom, every instant has exactly one
  // answer — the same "exactly one state answers every instant" rule the backend's own
  // join-window gate is built on.
  //
  // THE LOCAL CLOCK MAY ONLY EVER SHUT A DOOR, NEVER OPEN ONE (the F-15 doctrine: a
  // browser that decided for itself the room was open mounted a live consultation 26
  // hours early). So `joinState`, the SERVER's answer to "is it time", is consulted
  // before any comparison against `nowMs`, and the three clock comparisons below can
  // only ever move the phase further towards `closed` than the server has already said.
  //
  // BOUNDARIES ARE INCLUSIVE AT THE ENTERING EDGE (`>=`): the instant `closesAt` names
  // belongs to `closed`, not to the phase before it.
  let phase;
  if (status.joinState === "closed") phase = "closed";
  else if (status.joinState === "not_yet_open") phase = "waiting";
  else if (isConsultOver(status)) phase = "closed";
  else if (closesMs != null && nowMs >= closesMs) phase = "closed";
  else if (endsMs != null && nowMs >= endsMs) phase = "overrun";
  else if (endingSoonMs != null && nowMs >= endingSoonMs) phase = "endingSoon";
  else phase = "live";

  const secondsToEnd = secondsUntil(endsMs, nowMs);
  const secondsToClose = secondsUntil(closesMs, nowMs);

  // PRESENCE IS THE `room_watch` JOB'S VIEW, up to one poll interval old, and absent
  // entirely on a payload from a build that predates it. An absent sub-document is "we
  // know nothing about who is in the room", which renders nothing — not "nobody".
  const presence = status.presence || null;
  const soloDisconnectAt = presence?.soloDisconnectAt || null;
  const solo = presence?.soloSince
    ? {
        active: true,
        disconnectAt: soloDisconnectAt,
        secondsLeft: secondsUntil(toMs(soloDisconnectAt), nowMs),
        presentRoles: presence.presentRoles || [],
        snapshotAt: presence.snapshotAt || null,
      }
    : { ...EMPTY_SOLO, presentRoles: presence?.presentRoles || [],
        snapshotAt: presence?.snapshotAt || null };

  // "SOMEONE WAS REMOVED FOR BEING ALONE, AND THE ROOM IS EMPTY NOW." `soloEjectedAt` is
  // the `at` of the LAST `solo_ejections` entry and is append-only — it never goes back
  // to null — so on its own it would leave a "you were disconnected" panel up for the
  // rest of a call that has since resumed with both parties in it. The conjunction with
  // an empty room is what makes it a statement about NOW. It is deliberately not an
  // identity check: `/status` does not say WHO was ejected, and offering a rejoin button
  // to someone who is already inside costs a redundant button, whereas withholding it
  // from the person who was actually removed costs them the consultation.
  const ejected = (presence?.soloEjectedAt && (presence.presentCount || 0) === 0)
    ? { active: true, at: presence.soloEjectedAt }
    : { ...EMPTY_EJECTED, at: presence?.soloEjectedAt || null };

  // WHICH PHASES TICK. Only the two that render SECONDS, and only when the instant they
  // count to actually exists. The solo banner is deliberately excluded: it states whole
  // minutes over a presence snapshot up to 60 s old, so a per-second re-render would
  // claim a precision the server never offered — the coarse per-poll refresh keeps it
  // current at exactly the resolution it is entitled to.
  const countdownActive = (phase === "endingSoon" && secondsToEnd != null)
    || (phase === "overrun" && secondsToClose != null);

  return { phase, endsAt, closesAt, secondsToEnd, secondsToClose, solo, ejected, countdownActive };
}

export default roomPhase;
